/**
 * Utility functions for consistent number formatting across the application.
 */

/**
 * Formats a number as a currency string.
 * @param value The number to format.
 * @param currency The currency symbol (default: '€').
 * @returns A formatted currency string.
 */
export const formatCurrency = (value: number, currency: string = '€'): string => {
  return `${currency}${value.toFixed(2)}`;
};

/**
 * Formats a number representing hours.
 * @param hours The number of hours.
 * @param precision The number of decimal places (default: 0).
 * @returns A formatted string with 'h' suffix.
 */
export const formatHours = (hours: number, precision: number = 0): string => {
  return `${hours.toFixed(precision)}h`;
};

/**
 * Formats a number representing tokens with K/M suffixes.
 * @param tokens The number of tokens.
 * @returns A formatted string with K/M suffixes.
 */
export const formatTokens = (tokens: number): string => {
  if (tokens >= 1000000) {
    return (tokens / 1000000).toFixed(1) + 'M';
  }
  if (tokens >= 1000) {
    return (tokens / 1000).toFixed(0) + 'K';
  }
  return tokens.toString();
};

/**
 * Formats a number representing a rating.
 * @param rating The rating value.
 * @param precision The number of decimal places (default: 1).
 * @returns A formatted rating string.
 */
export const formatRating = (rating: number, precision: number = 1): string => {
  return rating.toFixed(precision);
};

/**
 * Formats a percentage value.
 * @param value The decimal value (e.g., 0.15 for 15%).
 * @param precision The number of decimal places (default: 1).
 * @returns A formatted percentage string.
 */
export const formatPercentage = (value: number, precision: number = 1): string => {
  return `${(value * 100).toFixed(precision)}%`;
};
